import React from 'react'
import { Copy, Pencil, Star, Trash2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { Tooltip } from '@/components/ui/tooltip'
import { ICP_REVIEW_DUE_DAYS, daysSince, isIcpComplete, type Icp } from '../types'

const MAX_CHIPS = 6

type Props = {
  icp: Icp
  canManage: boolean
  busy: boolean
  onEdit: (icp: Icp) => void
  onDuplicate: (icp: Icp) => void
  onMakePrimary: (icp: Icp) => void
  onDelete: (icp: Icp) => void
}

const Badge: React.FC<{ label: string; background: string; color: string }> = ({ label, background, color }) => (
  <span
    className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ backgroundColor: background, color }}
  >
    {label}
  </span>
)

const ChipRow: React.FC<{ label: string; values: string[] }> = ({ label, values }) => {
  const { currentTheme: t } = useTheme()
  if (values.length === 0) return null
  const shown = values.slice(0, MAX_CHIPS)
  const hidden = values.length - shown.length
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
      <span className="text-xs font-medium sm:w-28 shrink-0" style={{ color: t.mutedText }}>{label}</span>
      <div className="flex flex-wrap gap-1.5 min-w-0">
        {shown.map((value) => (
          <span
            key={value}
            className="text-xs px-2.5 py-1 rounded-full max-w-full truncate"
            style={{ backgroundColor: t.hoverBackground, color: t.text }}
          >
            {value}
          </span>
        ))}
        {hidden > 0 && (
          <span className="text-xs px-2 py-1" style={{ color: t.mutedText }}>+{hidden} more</span>
        )}
      </div>
    </div>
  )
}

function reviewedLabel(icp: Icp): string {
  const days = daysSince(icp.reviewedAt)
  if (days === 0) return 'Reviewed today'
  return `Reviewed ${days} day${days === 1 ? '' : 's'} ago`
}

const IconButton: React.FC<{
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: React.ReactNode
}> = ({ label, onClick, disabled = false, danger = false, children }) => {
  const { currentTheme: t } = useTheme()
  return (
    <Tooltip content={label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className="flex items-center justify-center h-8 w-8 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-40"
        style={{ color: danger ? t.errorText : t.secondrytext }}
        onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = t.hoverBackground }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
      >
        {children}
      </button>
    </Tooltip>
  )
}

const IcpCard: React.FC<Props> = ({ icp, canManage, busy, onEdit, onDuplicate, onMakePrimary, onDelete }) => {
  const { currentTheme: t } = useTheme()
  const complete = isIcpComplete(icp)
  const reviewDue = daysSince(icp.reviewedAt) >= ICP_REVIEW_DUE_DAYS
  const geography = [...icp.geography.countries, ...icp.geography.regions, ...icp.geography.cities]

  return (
    <article
      className="rounded-2xl p-4 sm:p-5 flex flex-col gap-4"
      style={{ backgroundColor: t.secondary, border: `1px solid ${t.border}` }}
      aria-label={`ICP ${icp.name}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h3 className="text-base font-semibold break-words" style={{ color: t.text }}>{icp.name}</h3>
          {icp.isPrimary && <Badge label="Primary" background={`${t.accent}18`} color={t.accent} />}
          {!complete && <Badge label="Incomplete" background={t.warningBackground} color={t.warningText} />}
          {reviewDue && <Badge label="Review due" background={t.warningBackground} color={t.warningText} />}
        </div>

        {canManage && (
          <div className="flex items-center gap-0.5 shrink-0">
            {!icp.isPrimary && (
              <IconButton label="Make primary" onClick={() => onMakePrimary(icp)} disabled={busy}>
                <Star size={15} />
              </IconButton>
            )}
            <IconButton label="Edit" onClick={() => onEdit(icp)} disabled={busy}>
              <Pencil size={15} />
            </IconButton>
            <IconButton label="Duplicate" onClick={() => onDuplicate(icp)} disabled={busy}>
              <Copy size={15} />
            </IconButton>
            <IconButton
              label={icp.isPrimary ? 'Make another ICP primary to delete this one' : 'Delete'}
              onClick={() => onDelete(icp)}
              disabled={busy || icp.isPrimary}
              danger
            >
              <Trash2 size={15} />
            </IconButton>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2.5">
        <ChipRow label="Industries" values={icp.industries} />
        <ChipRow label="Company sizes" values={icp.companySizes} />
        <ChipRow label="Job titles" values={icp.jobTitles} />
        <ChipRow label="Geography" values={geography} />
        <ChipRow label="Buying signals" values={icp.buyingSignals} />
      </div>

      {!complete && (
        <p className="text-xs" style={{ color: t.warningText }}>
          Add at least one industry, company size and job title so Zeph can use this ICP for prospecting and qualification.
        </p>
      )}

      <p className="text-xs" style={{ color: reviewDue ? t.warningText : t.mutedText }}>
        {reviewedLabel(icp)}
        {reviewDue ? ' — open and save it to confirm it’s still accurate.' : ''}
      </p>
    </article>
  )
}

export default React.memo(IcpCard)
